import React, { useMemo } from 'react';
import * as MuiIcons from '@mui/icons-material';
import { useColoredIcon } from '../../hooks/useColoredIcon';
import { ColoredIconProps } from './types';
import styles from './ColoredIcon.module.css';
import '../../styles/iconAnimations.css';

/**
 * Componente ColoredIcon
 * Renderiza ícones Material-UI com cores baseadas em categoria e tema
 * 
 * @example
 * <ColoredIcon 
 *   iconName="Dashboard"
 *   category="dashboard"
 *   size={24}
 *   animated={true}
 *   ariaLabel="Dashboard"
 * />
 */
export const ColoredIcon = React.memo(
  ({
    iconName,
    category,
    size = 24,
    disabled = false,
    animated = false,
    className = '',
    ariaLabel,
    statusIndicator = 'none',
    onClick,
    title,
    interactive = false
  }: ColoredIconProps) => {
    // Obter cores do hook
    const { color, hoverColor, semanticColor, isDarkMode } = useColoredIcon(
      category,
      statusIndicator
    );

    // Resolver ícone Material-UI dinamicamente
    const IconComponent = useMemo(() => {
      try {
        // Tentar encontrar o ícone na biblioteca Material-UI
        const iconKey = iconName as keyof typeof MuiIcons;
        const icon = MuiIcons[iconKey];
        
        if (!icon) {
          console.warn(
            `Ícone não encontrado: "${iconName}". Usando HelpOutline como fallback.`
          );
          return MuiIcons.HelpOutline;
        }
        
        return icon;
      } catch (error) {
        console.error(`Erro ao carregar ícone "${iconName}":`, error);
        return MuiIcons.HelpOutline;
      }
    }, [iconName]);

    // Construir classes CSS
    const classNames = useMemo(() => {
      const classes = [className];
      
      if (animated && !disabled) {
        classes.push('icon-animated');
      }
      
      if (disabled) {
        classes.push('icon-disabled');
      }

      if (statusIndicator && statusIndicator !== 'none') {
        classes.push('icon-status-indicator');
      }

      return classes.filter(Boolean).join(' ');
    }, [className, animated, disabled, statusIndicator]);

    // Estilo inline para cor
    const iconStyle = useMemo(() => {
      const baseStyle: React.CSSProperties = {
        fontSize: size,
        width: size,
        height: size,
        color: semanticColor || color,
        transition: animated ? 'color 250ms cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      };

      // Adicionar variáveis CSS para status indicator
      if (statusIndicator && statusIndicator !== 'none') {
        (baseStyle as any)['--status-color'] = semanticColor || color;
      }

      return baseStyle;
    }, [size, color, semanticColor, animated, statusIndicator]);

    // Aria-label padrão se não fornecido
    const finalAriaLabel = useMemo(() => {
      if (ariaLabel) return ariaLabel;
      
      // Gerar aria-label baseado no nome do ícone e categoria
      const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);
      const iconLabel = iconName
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toLowerCase();
      
      return `${categoryLabel} - ${iconLabel}`;
    }, [ariaLabel, iconName, category]);

    // Atributos de acessibilidade
    const a11yProps = useMemo(() => {
      const props: any = {
        'aria-label': finalAriaLabel,
        role: 'img'
      };

      if (interactive) {
        props.tabIndex = 0;
        props.role = 'button';
      }

      return props;
    }, [finalAriaLabel, interactive]);

    return (
      <IconComponent
        className={classNames}
        style={iconStyle}
        onClick={onClick}
        title={title || finalAriaLabel}
        {...a11yProps}
      />
    );
  },
  (prevProps, nextProps) => {
    // Comparação customizada para memoização
    return (
      prevProps.iconName === nextProps.iconName &&
      prevProps.category === nextProps.category &&
      prevProps.size === nextProps.size &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.animated === nextProps.animated &&
      prevProps.className === nextProps.className &&
      prevProps.ariaLabel === nextProps.ariaLabel &&
      prevProps.statusIndicator === nextProps.statusIndicator &&
      prevProps.onClick === nextProps.onClick &&
      prevProps.title === nextProps.title &&
      prevProps.interactive === nextProps.interactive
    );
  }
);

ColoredIcon.displayName = 'ColoredIcon';

export default ColoredIcon;
